import React from 'react';
import { Eye, Edit, Printer, IndianRupee, Calendar, CreditCard, User, Phone, MoreVertical, FileText, Activity, CheckCircle, AlertCircle, Share2, XCircle, Wallet } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { useBillingUtils } from '@/hooks/useBillingUtils';
// Import centralized types
import type { Invoice } from '@/types';

function shareOnWhatsApp(invoice: Invoice, formatDate: (d: string) => string) {
  const amount = Number(invoice.total_amount).toLocaleString('en-IN');
  const statusText =
    invoice.payment_status === 'paid'    ? 'fully paid ✓' :
    invoice.payment_status === 'partial' ? `partial — ₹${Number(invoice.balance_due).toLocaleString('en-IN')} still due` :
                                           'pending payment';
  const lines = [
    `Dear ${invoice.patient_name},`,
    ``,
    `Your bill (${invoice.invoice_number}) dated ${formatDate(invoice.invoice_date)}`,
    `Amount: ₹${amount} — ${statusText}`,
    ``,
    `Thank you.`,
  ];
  const msg = encodeURIComponent(lines.join('\n'));
  const phone = invoice.patient_phone?.replace(/\D/g, '');
  const url = phone
    ? `https://wa.me/91${phone}?text=${msg}`
    : `https://wa.me/?text=${msg}`;
  window.open(url, '_blank', 'noopener,noreferrer');
}

interface InvoiceStatusBadgeProps {
    status: 'paid' | 'unpaid' | 'partial' | 'cancelled';
    showIcon?: boolean;
    size?: 'sm' | 'md' | 'lg';
}

export function InvoiceStatusBadge({ status, showIcon = true, size = 'md' }: InvoiceStatusBadgeProps) {
    const { getStatusConfig } = useBillingUtils();
    const statusConfig = getStatusConfig(status);

    return (
        <Badge
            className={cn(
                "flex items-center gap-1 font-medium border",
                size === 'sm' ? 'text-xs px-2 py-1' : size === 'lg' ? 'text-sm px-3 py-1.5' : 'text-xs px-2 py-1',
                statusConfig.className
            )}
            style={{
                backgroundColor: statusConfig.bgColor,
                color: statusConfig.textColor,
                borderColor: statusConfig.borderColor
            }}
        >
            {showIcon && statusConfig.icon}
            <span>{statusConfig.label}</span>
        </Badge>
    );
}

interface InvoiceAmountDisplayProps {
    totalAmount: number;
    paidAmount?: number;
    balanceAmount?: number;
    paymentMethod: string;
    layout?: 'compact' | 'detailed';
}

export function InvoiceAmountDisplay({
                                         totalAmount,
                                         paidAmount = 0,
                                         balanceAmount = 0,
                                         paymentMethod,
                                         layout = 'detailed'
                                     }: InvoiceAmountDisplayProps) {
    const { formatCurrency, getPaymentMethodIcon } = useBillingUtils();

    if (layout === 'compact') {
        return (
            <div className="text-right">
                <div className="font-bold text-base text-foreground">
                    {formatCurrency(totalAmount)}
                </div>
                {balanceAmount > 0 && (
                    <div className="text-xs text-warning font-medium">
                        Due: {formatCurrency(balanceAmount)}
                    </div>
                )}
            </div>
        );
    }

    return (
        <div className="text-right space-y-1">
            <div className="text-xl font-bold text-foreground">
                {formatCurrency(totalAmount)}
            </div>
            {balanceAmount > 0 && (
                <div className="text-sm text-warning font-medium">
                    Due: {formatCurrency(balanceAmount)}
                </div>
            )}
            {paidAmount > 0 && balanceAmount > 0 && (
                <div className="text-xs text-muted-foreground">
                    Paid: {formatCurrency(paidAmount)}
                </div>
            )}
            <div className="flex items-center justify-end gap-1 text-xs text-muted-foreground">
                {getPaymentMethodIcon(paymentMethod)}
                {paymentMethod}
            </div>
        </div>
    );
}

interface InvoiceActionButtonsProps {
    invoice: Invoice;
    layout?: 'horizontal' | 'vertical';
    variant?: 'patient' | 'clinic';
    onView: (invoice: Invoice) => void;
    onEdit: (invoice: Invoice) => void;
    onPrint: (invoice: Invoice) => void;
    onPayment?: (invoice: Invoice) => void;
    onViewPatient?: (patientId: string) => void;
    onMoreActions?: (invoice: Invoice) => void;
}

export function InvoiceActionButtons({
                                         invoice,
                                         layout = 'horizontal',
                                         variant = 'patient',
                                         onView,
                                         onEdit,
                                         onPrint,
                                         onPayment,
                                         onViewPatient,
                                         onMoreActions
                                     }: InvoiceActionButtonsProps) {
    const containerClass = layout === 'vertical' ? 'flex flex-col gap-2' : 'flex gap-1.5';
    const buttonSize = layout === 'vertical' ? 'sm' : 'sm';

    const viewButton = (
        <Button
            variant="outline"
            size={buttonSize}
            onClick={() => onView(invoice)}
            className="flex items-center gap-1"
        >
            <Eye className="w-3 h-3" />
            View
        </Button>
    );

    const editButton = (
        <Button
            variant="outline"
            size={buttonSize}
            onClick={() => onEdit(invoice)}
            className="flex items-center gap-1 border-primary/20 hover:bg-primary/5 text-primary"
        >
            <Edit className="w-3 h-3" />
            Edit
        </Button>
    );

    const printButton = (
        <Button
            variant="outline"
            size={buttonSize}
            onClick={() => onPrint(invoice)}
            className="flex items-center gap-1"
        >
            <Printer className="w-3 h-3" />
            {layout === 'horizontal' ? 'Print' : <span className="sr-only">Print</span>}
        </Button>
    );

    const paymentButton = invoice.balance_due > 0 && onPayment && (
        <Button
            size={buttonSize}
            onClick={() => onPayment(invoice)}
            className={cn(
                "flex items-center gap-1 bg-success hover:bg-success/80 text-success-foreground",
                layout === 'vertical' ? 'w-full' : ''
            )}
        >
            <IndianRupee className="w-3 h-3" />
            {variant === 'patient' ? 'Pay' : 'Record Payment'}
        </Button>
    );

    if (layout === 'vertical') {
        return (
            <div className={containerClass}>
                <div className="flex gap-2">
                    {viewButton}
                    {editButton}
                    {printButton}
                </div>
                {paymentButton}
            </div>
        );
    }

    return (
        <div className={containerClass}>
            {viewButton}
            {editButton}
            {printButton}
            {paymentButton}
            {onMoreActions && (
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => onMoreActions(invoice)}>
                    <MoreVertical className="w-4 h-4" />
                </Button>
            )}
        </div>
    );
}

interface InvoiceCardProps {
    invoice: Invoice;
    variant: 'patient' | 'clinic';
    onView: (invoice: Invoice) => void;
    onEdit?: (invoice: Invoice) => void;
    onPrint: (invoice: Invoice) => void;
    onPayment?: (invoice: Invoice) => void;
    onCancel?: (invoice: Invoice) => void;
    onApplyAdvance?: (invoice: Invoice) => void;
    onViewPatient?: (patientId: string) => void;
    className?: string;
}

export function InvoiceCard({
                                invoice,
                                variant,
                                onView,
                                onEdit,
                                onPrint,
                                onPayment,
                                onCancel,
                                onApplyAdvance,
                                onViewPatient,
                                className
                            }: InvoiceCardProps) {
    const { formatDate, formatCurrency } = useBillingUtils();

    // Get payment status styling with overdue detection
    const getPaymentStatusStyling = (status: string, invoiceDate: string) => {
        const isOverdue = status === 'unpaid' &&
            new Date(invoiceDate) < new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);

        if (isOverdue) {
            return {
                borderClass: 'border-l-destructive',
                bgClass: 'bg-destructive/5',
                statusIcon: <AlertCircle className="w-4 h-4 text-destructive animate-pulse" />,
                statusColor: 'text-destructive',
                isOverdue: true
            };
        }

        switch (status) {
            case 'paid':
                return {
                    borderClass: 'border-l-success',
                    bgClass: 'bg-success/5 hover:bg-success/10',
                    statusIcon: <CheckCircle className="w-4 h-4 text-success" />,
                    statusColor: 'text-success',
                    isOverdue: false
                };
            case 'unpaid':
                return {
                    borderClass: 'border-l-warning',
                    bgClass: 'bg-warning/5 hover:bg-warning/10',
                    statusIcon: <AlertCircle className="w-4 h-4 text-warning" />,
                    statusColor: 'text-warning',
                    isOverdue: false
                };
            case 'partial':
                // Blue — money has been received, still outstanding
                return {
                    borderClass: 'border-l-primary',
                    bgClass: 'bg-primary/5 hover:bg-primary/10',
                    statusIcon: <Activity className="w-4 h-4 text-primary" />,
                    statusColor: 'text-primary',
                    isOverdue: false
                };
            case 'cancelled':
                return {
                    borderClass: 'border-l-destructive',
                    bgClass: 'bg-destructive/5 hover:bg-destructive/10',
                    statusIcon: <AlertCircle className="w-4 h-4 text-destructive" />,
                    statusColor: 'text-destructive',
                    isOverdue: false
                };
            default:
                return {
                    borderClass: 'border-l-muted',
                    bgClass: 'bg-muted/5',
                    statusIcon: <FileText className="w-4 h-4 text-muted-foreground" />,
                    statusColor: 'text-muted-foreground',
                    isOverdue: false
                };
        }
    };

    const statusStyling = getPaymentStatusStyling(invoice.payment_status, invoice.invoice_date);

    if (variant === 'patient') {
        return (
            <div className={cn(
                "p-3 hover:bg-muted/30 transition-colors border-l-4",
                statusStyling.borderClass,
                className
            )}>
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <div>
                            <div className="font-semibold text-foreground">{invoice.invoice_number}</div>
                            <div className="text-sm text-muted-foreground">{formatDate(invoice.invoice_date)}</div>
                            {statusStyling.isOverdue && (
                                <div className="text-xs text-destructive font-medium">Overdue</div>
                            )}
                        </div>
                    </div>

                    <div className="flex items-center gap-4">
                        <div className="text-right">
                            <div className="text-base font-bold text-foreground">
                                {formatCurrency(invoice.total_amount)}
                            </div>
                            {invoice.payment_status === 'paid' ? (
                                <div className="text-xs text-success">Paid</div>
                            ) : invoice.payment_status === 'partial' ? (
                                <div className="text-xs text-primary">
                                    Due: {formatCurrency(invoice.balance_due)}
                                </div>
                            ) : invoice.payment_status === 'cancelled' ? (
                                <div className="text-xs text-destructive">Cancelled</div>
                            ) : (
                                <div className="text-xs text-warning">Due: {formatCurrency(invoice.balance_due)}</div>
                            )}
                        </div>

                        <div className="flex gap-1">
                            <Button variant="ghost" size="sm" onClick={() => onView(invoice)} className="h-8 w-8 p-0" title="View invoice">
                                <Eye className="w-4 h-4" />
                            </Button>
                            {onEdit && invoice.payment_status !== 'paid' && invoice.payment_status !== 'cancelled' && (
                                <Button variant="ghost" size="sm" onClick={() => onEdit(invoice)} className="h-8 w-8 p-0" title="Edit invoice">
                                    <Edit className="w-4 h-4" />
                                </Button>
                            )}
                            <Button variant="ghost" size="sm" onClick={() => onPrint(invoice)} className="h-8 w-8 p-0" title="Print invoice">
                                <Printer className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => shareOnWhatsApp(invoice, formatDate)} className="h-8 w-8 p-0 text-success hover:text-success hover:bg-success/10" title="Share on WhatsApp">
                                <Share2 className="w-4 h-4" />
                            </Button>
                            {invoice.balance_due > 0 && onPayment && invoice.payment_status !== 'cancelled' && (
                                <Button size="sm" onClick={() => onPayment(invoice)} className="h-8 px-3 text-xs bg-success hover:bg-success/80 text-success-foreground">
                                    Pay
                                </Button>
                            )}
                            {invoice.balance_due > 0 && onApplyAdvance && invoice.payment_status !== 'cancelled' && (
                                <Button variant="ghost" size="sm" onClick={() => onApplyAdvance(invoice)} className="h-8 w-8 p-0 text-primary/60 hover:text-primary hover:bg-primary/10" title="Apply advance balance to this invoice">
                                    <Wallet className="w-4 h-4" />
                                </Button>
                            )}
                            {onCancel && invoice.payment_status !== 'cancelled' && (
                                <Button variant="ghost" size="sm" onClick={() => onCancel(invoice)} className="h-8 w-8 p-0 text-destructive/50 hover:text-destructive hover:bg-destructive/10" title="Cancel invoice">
                                    <XCircle className="w-4 h-4" />
                                </Button>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Clinic variant - Simple design
    return (
        <Card className={cn(
            "medical-card hover:shadow-md transition-shadow border-l-4",
            statusStyling.borderClass,
            className
        )}>
            <CardContent className="p-3">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4 min-w-0">
                        <div className="min-w-0">
                            <div className="font-semibold text-foreground truncate">{invoice.patient_name}</div>
                            <div className="text-sm text-muted-foreground">
                                {invoice.invoice_number} • {formatDate(invoice.invoice_date)}
                            </div>
                            {/* Doctor name — important in multi-doctor clinic */}
                            {invoice.doctor_name && (
                                <div className="text-xs text-muted-foreground">{invoice.doctor_name}</div>
                            )}
                            {/* Overdue badge */}
                            {statusStyling.isOverdue && (
                                <div className="text-xs text-destructive font-medium mt-0.5">Overdue</div>
                            )}
                        </div>
                    </div>

                    <div className="flex items-center gap-4 flex-shrink-0">
                        <div className="text-right">
                            <div className="text-base font-bold text-foreground">
                                {formatCurrency(invoice.total_amount)}
                            </div>
                            {invoice.payment_status === 'paid' ? (
                                <div className="text-xs text-success">Paid</div>
                            ) : invoice.payment_status === 'cancelled' ? (
                                <div className="text-xs text-destructive">Cancelled</div>
                            ) : invoice.payment_status === 'partial' ? (
                                <div className="text-xs text-primary">
                                    Due: {formatCurrency(invoice.balance_due)}
                                    <span className="text-muted-foreground ml-1">
                                        (paid {formatCurrency(invoice.paid_amount)})
                                    </span>
                                </div>
                            ) : (
                                <div className="text-xs text-warning">Due: {formatCurrency(invoice.balance_due)}</div>
                            )}
                        </div>

                        <div className="flex gap-1">
                            <Button variant="ghost" size="sm" onClick={() => onView(invoice)} className="h-8 w-8 p-0" title="View invoice">
                                <Eye className="w-4 h-4" />
                            </Button>

                            {/* Edit — hidden for paid and cancelled invoices */}
                            {onEdit && invoice.payment_status !== 'paid' && invoice.payment_status !== 'cancelled' && (
                                <Button variant="ghost" size="sm" onClick={() => onEdit(invoice)} className="h-8 w-8 p-0" title="Edit invoice">
                                    <Edit className="w-4 h-4" />
                                </Button>
                            )}

                            <Button variant="ghost" size="sm" onClick={() => onPrint(invoice)} className="h-8 w-8 p-0" title="Print invoice">
                                <Printer className="w-4 h-4" />
                            </Button>

                            <Button variant="ghost" size="sm" onClick={() => shareOnWhatsApp(invoice, formatDate)} className="h-8 w-8 p-0 text-success hover:text-success hover:bg-success/10" title="Share on WhatsApp">
                                <Share2 className="w-4 h-4" />
                            </Button>

                            {invoice.balance_due > 0 && onPayment && invoice.payment_status !== 'cancelled' && (
                                <Button size="sm" onClick={() => onPayment(invoice)} className="h-8 px-3 text-xs bg-success hover:bg-success/80 text-success-foreground">
                                    Pay
                                </Button>
                            )}

                            {invoice.balance_due > 0 && onApplyAdvance && invoice.payment_status !== 'cancelled' && (
                                <Button variant="ghost" size="sm" onClick={() => onApplyAdvance(invoice)} className="h-8 w-8 p-0 text-primary/60 hover:text-primary hover:bg-primary/10" title="Apply advance balance to this invoice">
                                    <Wallet className="w-4 h-4" />
                                </Button>
                            )}

                            {onCancel && invoice.payment_status !== 'cancelled' && (
                                <Button variant="ghost" size="sm" onClick={() => onCancel(invoice)} className="h-8 w-8 p-0 text-destructive/50 hover:text-destructive hover:bg-destructive/10" title="Cancel invoice">
                                    <XCircle className="w-4 h-4" />
                                </Button>
                            )}

                            {onViewPatient && (
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => onViewPatient(invoice.patient_id!)}
                                    className="h-8 w-8 p-0"
                                    title="View patient profile"
                                >
                                    <User className="w-4 h-4" />
                                </Button>
                            )}
                        </div>
                    </div>
                </div>
            </CardContent>
        </Card>
    );
}